"use client";

import { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Link from "@/lib/localized-link";
import { useLocalizedRouter } from "@/lib/localized-router";
import { useAuthHref } from "@/lib/use-return-to";
import { sellProperty, RateLimitError, UserInputError, NetworkError } from "@/lib/api";
import { captureError } from "@/lib/sentry";
import { trackEvent } from "@/lib/tracking";
import { captureEvent } from "@/lib/tracking";
import { useT } from "@/lib/locale-context";
import { useAuth } from "@/lib/auth-context";
import type { SellResult } from "@/types";
import LocationPicker, { type SelectedLocation } from "@/components/LocationPicker";
import { Field } from "@/components/calc/Field";
import MobileBottomChromeOffset from "@/components/MobileBottomChromeOffset";
import RateLimitBanner from "@/components/RateLimitBanner";
import RecentActivity from "@/components/RecentActivity";
import { recordCalculate } from "@/lib/recent-activity";
import { AlertTriangle, Lock, Check } from "lucide-react";

// Same pattern as /analyze: the seller result card is a large tree
// (~550 LoC) that only renders after the user submits the calculator
// form. Dynamic-import defers it out of the first-paint critical path.
const SellerResultCard = dynamic(
  () => import("@/components/SellerResultCard"),
  { ssr: false },
);
// The skeleton is tiny and must paint synchronously on submit: when it
// was a lazy chunk the form collapsed to nothing, then the skeleton
// arrived, then the card - two counted layout shifts (CLS 1.1 p75 on
// phones, 0.8 on desktop in PostHog, Aug-Sep 2026).
import SellerResultCardSkeleton from "@/components/SellerResultCardSkeleton";

// Set while a result is on screen so a same-tab return (back from a
// residence link) restores it; a fresh visit never swaps the form for a
// stored result after paint.
const RESTORE_FLAG = "menzil-calc-restore";
function markRestorable(on: boolean) {
  try {
    if (on) window.sessionStorage.setItem(RESTORE_FLAG, "1");
    else window.sessionStorage.removeItem(RESTORE_FLAG);
  } catch { /* disabled storage - non-fatal */ }
}
import AnalysisProgress from "@/components/AnalysisProgress";
import { sourceOf } from "@/lib/calc-place";
import { useReportAnalysisBusy } from "@/lib/analysis-busy";
import { FIELD, SELECTED, UNSELECTED } from "@/lib/ui-classes";

export default function SellPage() {
  const t = useT();
  const router = useLocalizedRouter();
  const { refreshTokens, user } = useAuth();
  const registerHref = useAuthHref("register");
  // No "· 1" cost chip on the Evaluate button any more: a valuation costs
  // the person nothing on any account (owner, 2026-10-18).
  const [loading, setLoading] = useState(false);
  useReportAnalysisBusy(loading);
  const [error, setError] = useState("");
  const [rateLimitError, setRateLimitError] = useState<RateLimitError | null>(null);
  const [result, setResult] = useState<SellResult | null>(null);

  // Anonymous calculations have no /h permalink (signed-in ones
  // redirect there and survive any navigation) - so persist the latest
  // anon result and restore it on mount: opening a residence link from
  // the result and coming BACK used to land on a blank form.
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem("menzil-anon-calc-result");
      if (!raw) return;
      if (window.sessionStorage.getItem(RESTORE_FLAG) !== "1") return;
      const parsed = JSON.parse(raw) as { result?: SellResult; ts?: number };
      if (!parsed?.result || typeof parsed.ts !== "number") return;
      if (Date.now() - parsed.ts > 60 * 60 * 1000) {
        window.localStorage.removeItem("menzil-anon-calc-result");
        return;
      }
      setResult(parsed.result);
    } catch { /* disabled storage - non-fatal */ }
    // Mount-only restore.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Warm the result-card chunk while the visitor fills the form, so the
  // swap to the result never waits on a network round-trip.
  useEffect(() => {
    const id = window.setTimeout(() => { void import("@/components/SellerResultCard"); }, 2000);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => { markRestorable(result != null); }, [result]);

  const [form, setForm] = useState({
    // "apartment" | "house" - controls which fields render and which
    // model the backend dispatches to. Houses use land_area_sot instead
    // of floor / total_floors, and skip the building-type picker
    // entirely (the houses model has no "new build" vs "old build"
    // dichotomy - it's a single category).
    property_type: "apartment" as "apartment" | "house",
    area_m2: "",
    room_count: "2",
    floor: "",
    total_floors: "",
    land_area_sot: "",
    category_name: "Yeni tikili",
    // Smart default: ~85% of active sale apartments in the index have
    // repair, so the pre-checked state matches the typical property (a
    // default reads as a recommendation - make it the dominant truth).
    has_repair: true,
    has_bill_of_sale: false,
    description: "",
  });
  const [location, setLocation] = useState<SelectedLocation | null>(null);
  // A rental read by the intake (?deal=rent): the result opens on its
  // rent side; the valuation itself is sale + rent either way.
  const [leadRent, setLeadRent] = useState(false);
  // Prefill from the query string: the chat's "prepare_valuation" card
  // links here with the facts the user gave (type, area, rooms, floor,
  // floors, land_sot, repair, deed, building, lat/lng/label/district).
  // Read once on mount; window.location keeps this out of Suspense.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const q = new URLSearchParams(window.location.search);
    if (![...q.keys()].length) return;
    if (q.get("deal") === "rent") setLeadRent(true);
    const num = (k: string) => { const v = q.get(k); return v != null && v !== "" && Number.isFinite(Number(v)) ? v : null; };
    const flag = (k: string) => { const v = q.get(k); return v === "1" ? true : v === "0" ? false : null; };
    setForm((prev) => {
      const next = { ...prev };
      if (q.get("type") === "house" || q.get("type") === "apartment") next.property_type = q.get("type") as "apartment" | "house";
      if (num("area")) next.area_m2 = String(Number(num("area")));
      if (num("rooms")) next.room_count = String(parseInt(num("rooms")!, 10));
      if (num("floor")) next.floor = String(parseInt(num("floor")!, 10));
      if (num("floors")) next.total_floors = String(parseInt(num("floors")!, 10));
      if (num("land_sot")) next.land_area_sot = String(Number(num("land_sot")));
      if (q.get("building") === "old") next.category_name = "Köhnə tikili";
      if (q.get("building") === "new") next.category_name = "Yeni tikili";
      const repair = flag("repair");
      if (repair !== null) next.has_repair = repair;
      const deed = flag("deed");
      if (deed !== null) next.has_bill_of_sale = deed;
      return next;
    });
    const lat = num("lat"), lng = num("lng");
    if (lat && lng) {
      const cslug = q.get("cslug") || null;
      const label = q.get("label") || "";
      // How the linked point was picked: a residence (by its id), the
      // map's "evaluate here" (a pin, no label), or a place named
      // elsewhere (a point that never snaps to a residence). The field
      // names a pin or a named point itself (the nearest endpoint), and
      // a link without a label gets the place's name, not coordinates.
      setLocation({
        latitude: Number(lat),
        longitude: Number(lng),
        district: q.get("district") || null,
        complex_korter_slug: cslug,
        complex_slug: cslug,
        display_label: label || `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}`,
        source: cslug ? "complex" : label ? "address" : "pin",
        resolved: cslug && label ? { kind: "residence", name: label, area: q.get("district") || null } : null,
      });
    }
    if (q.get("src")) trackEvent("calculate.prefill", { src: q.get("src") });
  }, []);
  // Synchronous re-entry guard for the submit handler. ``setLoading(true)``
  // only disables the button after React commits the next render, leaving
  // a 50-200 ms window on slow mobile devices where a rapid second tap
  // (or panicked re-tap after an error) fires a duplicate POST. Same
  // pattern used by /analyze; see that file for the production data.
  const submittingRef = useRef(false);

  // When a calculation runs, pull the result area to the top so the user
  // sees the result (and its skeleton) from the top instead of being
  // stranded mid-page. Keyed on `loading` (only true during a live
  // submit). scroll-mt on the wrapper keeps it clear of the sticky header.
  const resultRef = useRef<HTMLDivElement | null>(null);
  // Mirrors /analyze: scroll to the skeleton on submit, then an INSTANT
  // jump to the top when the fresh result lands (a smooth scroll gets
  // cancelled by the skeleton->result reflow, stranding the user
  // mid-page next to the form).
  const ranRef = useRef(false);
  useEffect(() => {
    if (loading) {
      ranRef.current = true;
      resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (ranRef.current && result) {
      ranRef.current = false;
      // Triple pin: the first jump runs before the result's images /
      // charts mount; their reflows (and the router's scroll handling)
      // can drag the viewport back down, so re-pin on the next frame
      // and once more after the early reflows settle.
      window.scrollTo({ top: 0, behavior: "auto" });
      requestAnimationFrame(() => window.scrollTo({ top: 0 }));
      setTimeout(() => window.scrollTo({ top: 0 }), 150);
    }
  }, [loading, result]);

  const update = (field: string, value: string | boolean) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  // ── Validation (per-field) ──
  const isHouse = form.property_type === "house";
  const floor = Number(form.floor);
  const totalFloors = Number(form.total_floors);
  const area = Number(form.area_m2);
  const rooms = Number(form.room_count);
  const landAreaSot = Number(form.land_area_sot);

  const fieldErrors: {
    area?: string;
    floor?: string;
    total_floors?: string;
    rooms?: string;
    land_area_sot?: string;
  } = {};
  if (form.area_m2 && area < 0) fieldErrors.area = t("sell.validation.negative");
  else if (area > 0 && area < 10) fieldErrors.area = t("sell.validation.areaTooSmall");
  else if (area > 500) fieldErrors.area = t("sell.validation.areaTooLarge");

  if (isHouse) {
    // House mode: floor/total_floors don't apply; land plot size does.
    // We don't enforce an upper cap here - the backend's pydantic
    // schema already caps at 10000 sot, which is well past anything
    // realistic for Baku.
    if (form.land_area_sot && landAreaSot < 0) {
      fieldErrors.land_area_sot = t("sell.validation.negative");
    }
  } else {
    if (form.floor && floor < 0) fieldErrors.floor = t("sell.validation.negative");
    else if (floor > 0 && totalFloors > 0 && floor > totalFloors) fieldErrors.floor = t("sell.validation.floorExceedsTotal");

    if (form.total_floors && totalFloors < 0) fieldErrors.total_floors = t("sell.validation.negative");
    else if (totalFloors > 60) fieldErrors.total_floors = t("sell.validation.tooManyFloors");
    else if (floor > 0 && totalFloors <= 0) fieldErrors.total_floors = t("sell.validation.totalFloorsRequired");
  }

  if (rooms > 12) fieldErrors.rooms = t("sell.validation.tooManyRooms");
  else if (rooms > 0 && area > 0 && area / rooms < 8) fieldErrors.rooms = t("sell.validation.tooManyRoomsForArea");

  const hasErrors = Object.keys(fieldErrors).length > 0;

  // The button is never dead: pressed with the form incomplete, it names
  // what is missing, the fields flash and the first one scrolls into
  // view (owner, 2026-10-11: "users are not understanding why the
  // button is disabled"). `nudge` counts the presses so the flash
  // replays on every press.
  const [nudge, setNudge] = useState(0);
  const missing = {
    area: !(area > 0),
    floors: !isHouse && !(floor > 0 && totalFloors > 0),
    land: isHouse && !(landAreaSot > 0),
    rooms: !(rooms >= 1),
    location: location === null,
  };
  const missingNames = [
    missing.area ? t("sell.area").replace(/\s*\([^)]*\)\s*$/, "") : null,
    missing.floors ? t("sell.floorpair") : null,
    missing.land ? t("sell.landarea") : null,
    missing.rooms ? t("sell.rooms") : null,
    missing.location ? t("sell.location.label") : null,
  ].filter((x): x is string => !!x);
  const nudgeMissing = () => {
    setNudge((n) => n + 1);
    const first = (["area", "floors", "land", "rooms", "location"] as const).find((k) => missing[k]);
    if (first) {
      window.setTimeout(() => document.getElementById(`calc-f-${first}`)?.scrollIntoView({ block: "center", behavior: "smooth" }), 30);
    }
  };
  const flash = (k: keyof typeof missing) => (nudge > 0 && missing[k] ? "animate-nudge" : "");
  const missingNote = nudge > 0 && missingNames.length > 0 && (
    <p className="mt-2 text-center text-xs font-medium text-rose-600" role="alert">
      {t("sell.missing").replace("{fields}", missingNames.join(", "))}
    </p>
  );

  // Once an anonymous device is out of free analyses, swap the Evaluate
  // CTA for a sign-up link so mobile users can act from the sticky bar
  // without scrolling back to the top to see the RateLimitBanner.
  // /auth/register lets them pick email or Google. A signed-in account
  // is never refused for volume (owner, 2026-10-18), so the bar has no
  // upgrade variant: the only signed-in refusal is the unverified email,
  // which the top banner handles with its resend button.
  const rateLimitCta: { href: string; labelKey: "auth.signup" } | null =
    rateLimitError && !user ? { href: registerHref, labelKey: "auth.signup" } : null;

  const canSubmit =
    area > 0 &&
    rooms >= 1 &&
    location !== null &&
    !hasErrors &&
    (isHouse
      // Houses: floor / total_floors don't apply; require land_area_sot.
      ? landAreaSot > 0
      // Apartments: keep the existing floor + total_floors gate.
      : floor > 0 && totalFloors > 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !location) { if (!loading) nudgeMissing(); return; }
    if (submittingRef.current) return;
    submittingRef.current = true;

    trackEvent("calculate.submit", {
      location_source: sourceOf(location),
      has_bill_of_sale: form.has_bill_of_sale,
      has_repair: form.has_repair,
    });
    captureEvent("calculate_submitted", {
      location_source: sourceOf(location),
      category_name: form.category_name,
      has_bill_of_sale: form.has_bill_of_sale,
      has_repair: form.has_repair,
      room_count: Number(form.room_count),
    });

    setLoading(true);
    setError("");
    setRateLimitError(null);
    setResult(null);

    try {
      // Build the payload conditionally so house submissions don't
      // ship a stale floor/total_floors/category_name from a previous
      // apartment-mode session, and apartment submissions don't ship
      // an orphan land_area_sot. The backend validates per-property-type.
      const payload: Parameters<typeof sellProperty>[0] = {
        property_type: form.property_type,
        area_m2: Number(form.area_m2),
        room_count: Number(form.room_count),
        has_repair: form.has_repair,
        has_bill_of_sale: form.has_bill_of_sale,
        latitude: location.latitude,
        longitude: location.longitude,
        location_name: location.district || undefined,
        complex_korter_slug: location.complex_korter_slug || undefined,
        // How the point was picked and what: the backend snaps to a
        // residence only for a residence pick or a pin on one, and names
        // the area from the street, the place or the rayon polygon
        // (routes/sell.py, services/calc_place.py; analysis 1800).
        location_source: sourceOf(location),
        street_id: location.street_id ?? undefined,
        place_id: location.place_id ?? undefined,
        complex_slug: location.complex_slug ?? undefined,
        description: form.description || undefined,
        // Sale and rent together for both kinds: a house's rent is a
        // range (two seasonal prices in the dacha villages), and a box
        // without the house-rent model answers with the sale alone.
        mode: "both",
      };
      if (isHouse) {
        payload.land_area_sot = Number(form.land_area_sot);
      } else {
        payload.floor = Number(form.floor);
        payload.total_floors = Number(form.total_floors);
        payload.category_name = form.category_name;
      }
      const data = await sellProperty(payload);
      // Promote logged-in evaluations to permalinks immediately. All
      // history permalinks live under /analyze/{id} - a single dynamic
      // edge route (see the note in that file for why we collapsed the
      // two routes into one).
      if (data.history_id) {
        // Signed-in runs live at /h permalinks - drop any stale anon
        // result so it can't shadow the form on the next visit here.
        try { window.localStorage.removeItem("menzil-anon-calc-result"); } catch { /* ignore */ }
        router.replace(`/h?id=${data.history_id}`, { scroll: false });
        refreshTokens();
        return;
      }
      setResult(data);
      try {
        window.localStorage.setItem(
          "menzil-anon-calc-result",
          JSON.stringify({ result: data, ts: Date.now() }),
        );
      } catch { /* ignore */ }
      recordCalculate(data);
      refreshTokens();
      setLoading(false);
    } catch (err) {
      if (err instanceof RateLimitError) {
        setRateLimitError(err);
      } else if (err instanceof UserInputError) {
        setError(err.message);
      } else if (err instanceof NetworkError) {
        setError(t("error.network"));
      } else {
        const msg = err instanceof Error ? err.message : t("sell.failed");
        setError(msg);
        captureError(err, { source: "calculate", location: location?.district });
      }
      setLoading(false);
    } finally {
      submittingRef.current = false;
    }
  };

  const handleReset = () => {
    setResult(null);
    try { window.localStorage.removeItem("menzil-anon-calc-result"); } catch { /* ignore */ }
    setForm({
      property_type: "apartment",
      area_m2: "",
      room_count: "2",
      floor: "",
      total_floors: "",
      land_area_sot: "",
      category_name: "Yeni tikili",
      has_repair: true,
      has_bill_of_sale: false,
      description: "",
    });
    setLocation(null);
  };

  // Result container goes wider than the input form - the result uses
  // a 3:2 two-column grid (tier hero left, receipt/rail right) and at
  // max-w-6xl each tier card in the 3-across TierGrid came out tight
  // (~210px). max-w-7xl gives ~240px per card. The page shell +
  // CalcNav render in layout.tsx (persist across calculator switches).
  return (
      <main className={`${result || loading ? "max-w-7xl" : "max-w-6xl"} mx-auto px-4 sm:px-6 pb-24 lg:pb-10`}>
        {loading ? (
          <div ref={resultRef} className="relative scroll-mt-24">
            {/* Same wait grammar as /analyze: the skeleton is a dimmed
                curtain, the status card floats viewport-centered with
                the calculator illustration doing the talking. */}
            <div className="pointer-events-none opacity-40" aria-hidden>
              <SellerResultCardSkeleton />
            </div>
            <div className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center px-4">
              {/* The analysis's wait card: steps, the bar and the turning
                  mark (owner, 2026-10-19), with the calculator scene. */}
              <div className="pointer-events-auto w-full max-w-xl rounded-2xl shadow-[0_24px_64px_-24px_rgba(15,23,42,0.35)]">
                <AnalysisProgress variant="calculate" />
              </div>
            </div>
          </div>
        ) : !result ? (
          <>
            {/* Hero - title scale matches the mortgage + sale-tax
                calculator pages (text-3xl → 4xl) so the three CalcNav
                peers read as a consistent set. */}
            <div className="text-center mb-8 sm:mb-10 max-w-2xl mx-auto">
              <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
                {t("sell.title")}
              </h1>
              <p className="mt-4 text-base sm:text-lg text-gray-600 leading-relaxed">
                {t("sell.subtitle")}
              </p>
            </div>

            {error && (
              <div className="mb-4 max-w-2xl mx-auto rounded-2xl border border-red-200 bg-red-50 p-4 flex items-start gap-3 text-sm text-red-700">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-red-600" aria-hidden />
                <span className="min-w-0">{error}</span>
              </div>
            )}

            {rateLimitError && (
              // Hidden on < lg: the same content (lock icon + headline
              // + subtext + CTA) renders in the mobile sticky bottom
              // bar, so the inline banner up here is just duplicated
              // chrome that pushes the form down on a viewport that
              // already shows it elsewhere.
              <div className="hidden lg:block mb-4 max-w-2xl mx-auto">
                <RateLimitBanner error={rateLimitError} />
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Two-column layout at lg+:
                    Left (3/5) - property basics (area, rooms, floor,
                      type, toggles): dense grid of small fields.
                    Right (2/5) - LocationPicker which is naturally
                      tall (district → complex → map).
                  `items-start` lets the columns take natural heights
                  so the shorter one doesn't stretch to match. On
                  mobile everything collapses to a single column. */}
              {/* Two columns again from lg (owner, 2026-09-22: "let's
                  make the location picker on the desktop half-width and
                  the remaining half a squarish location picker ... it is
                  way too wide for inputs"): the property questions on
                  the left, the location and its map on the right, and
                  one column on phones. The card is wider than the
                  single-column version it replaces but each field is
                  narrower. */}
              <div className="mx-auto w-full max-w-3xl lg:max-w-5xl rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
              <div className="space-y-3 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0">
                {/* LEFT - property basics */}
                <div>
                  {/* One dense grid instead of five stacked rows (owner,
                      2026-09-21: "we can be more space efficient with the
                      calculate form, both on desktop and mobile, certain
                      things do not need to be this large"). Four columns
                      from sm: the two pickers take half a row each, the
                      three numbers a quarter each, and the room tiles and
                      switches a full row. Below sm it is two columns. */}
                  {/* One control answers what it is: new flat, old flat or
                      house. That removes the second picker entirely, and
                      with it the block that used to appear and disappear
                      (owner, 2026-09-21: "I don't like that certain things
                      take so much space unnecessarily, and we have so many
                      things moving"). Every mode then shows the same four
                      rows, and the second row always holds two fields:
                      area plus either the floor pair or the land area. */}
                  <div className="space-y-3">
                    <div>
                      <label className="mb-1 block text-[11px] font-semibold text-gray-500">
                        {t("sell.proptype")}
                      </label>
                      <div className="flex gap-1.5">
                        {([
                          ["flat-new", t("sell.buildtype.new")],
                          ["flat-old", t("sell.buildtype.old")],
                          ["house", t("sell.proptype.house")],
                        ] as const).map(([value, label]) => {
                          const on =
                            value === "house"
                              ? isHouse
                              : !isHouse &&
                                form.category_name === (value === "flat-new" ? "Yeni tikili" : "Köhnə tikili");
                          return (
                            <button
                              key={value}
                              type="button"
                              onClick={() => {
                                if (value === "house") { update("property_type", "house"); return; }
                                update("property_type", "apartment");
                                update("category_name", value === "flat-new" ? "Yeni tikili" : "Köhnə tikili");
                              }}
                              className={`h-9 flex-1 rounded-lg border px-1 text-xs font-semibold transition-colors ${
                                on ? SELECTED : UNSELECTED
                              }`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-x-3">
                      <div id="calc-f-area" key={`area-${nudge}`} className={flash("area")}>
                        <Field
                          size="sm"
                          label={t("sell.area").replace(/\s*\([^)]*\)\s*$/, "")}
                          value={form.area_m2}
                          onChange={(v) => update("area_m2", v)}
                          unit="m²"
                          placeholder="85"
                          min={0}
                          max={100000}
                          error={!!fieldErrors.area || (nudge > 0 && missing.area)}
                        />
                        {fieldErrors.area && (
                          <p className="mt-1 text-[11px] text-rose-600">{fieldErrors.area}</p>
                        )}
                      </div>

                      {isHouse ? (
                        <div id="calc-f-land" key={`land-${nudge}`} className={flash("land")}>
                          <Field
                            size="sm"
                            label={t("sell.landarea")}
                            value={form.land_area_sot}
                            onChange={(v) => update("land_area_sot", v)}
                            unit={t("sell.landarea.sot")}
                            placeholder="6"
                            step="0.1"
                            min={0}
                            max={1000}
                            error={!!fieldErrors.land_area_sot || (nudge > 0 && missing.land)}
                          />
                          {fieldErrors.land_area_sot && (
                            <p className="mt-1 text-[11px] text-rose-600">{fieldErrors.land_area_sot}</p>
                          )}
                        </div>
                      ) : (
                        /* Floor and total floors share one box, the way a
                           listing writes them: 9 / 16. Two labels and two
                           bordered fields for one fact was a waste of a
                           row. */
                        <label id="calc-f-floors" key={`floors-${nudge}`} className={`block ${flash("floors")}`}>
                          <span className="mb-1 block text-[11px] font-semibold text-gray-500">
                            {t("sell.floorpair")}
                          </span>
                          <span
                            className={`flex items-center rounded-lg border ${
                              fieldErrors.floor || fieldErrors.total_floors || (nudge > 0 && missing.floors)
                                ? "field-surface border-rose-300 bg-white focus-within:ring-1 focus-within:ring-rose-200"
                                : FIELD
                            }`}
                          >
                            <input
                              type="number"
                              inputMode="numeric"
                              min={0}
                              max={200}
                              value={form.floor}
                              onChange={(e) => update("floor", e.target.value)}
                              placeholder="9"
                              aria-label={t("sell.floor")}
                              className="w-full bg-transparent px-2.5 py-1.5 text-center text-[13px] font-semibold text-gray-900 outline-none"
                            />
                            <span className="select-none text-xs text-gray-400">/</span>
                            <input
                              type="number"
                              inputMode="numeric"
                              min={0}
                              max={200}
                              value={form.total_floors}
                              onChange={(e) => update("total_floors", e.target.value)}
                              placeholder="16"
                              aria-label={t("sell.totalfloors")}
                              className="w-full bg-transparent px-2.5 py-1.5 text-center text-[13px] font-semibold text-gray-900 outline-none"
                            />
                          </span>
                          {(fieldErrors.floor || fieldErrors.total_floors) && (
                            <span className="mt-1 block text-[11px] text-rose-600">
                              {fieldErrors.floor || fieldErrors.total_floors}
                            </span>
                          )}
                        </label>
                      )}
                    </div>

                    <div id="calc-f-rooms" key={`rooms-${nudge}`} className={flash("rooms")}>
                      <label className="mb-1 block text-[11px] font-semibold text-gray-500">
                        {t("sell.rooms")}
                      </label>
                      <div className={`flex gap-1 ${nudge > 0 && missing.rooms ? "rounded-lg ring-2 ring-rose-300 ring-offset-2" : ""}`}>
                        {["1", "2", "3", "4", "5"].map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => update("room_count", n)}
                            className={`h-8 flex-1 rounded-md border text-[13px] font-semibold transition-colors ${
                              form.room_count === n ? SELECTED : UNSELECTED
                            }`}
                          >
                            {n}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => { if (Number(form.room_count) < 6) update("room_count", "6"); }}
                          className={`h-8 flex-1 rounded-md border text-[13px] font-semibold transition-colors ${
                            Number(form.room_count) >= 6 ? SELECTED : UNSELECTED
                          }`}
                        >
                          6+
                        </button>
                      </div>
                      {Number(form.room_count) >= 6 && (
                        <div className="mt-2 max-w-[8rem]">
                          <Field
                            label={t("sell.rooms")}
                            value={form.room_count}
                            onChange={(v) => update("room_count", v)}
                            unit=""
                            size="sm"
                            min={6}
                            max={12}
                          />
                        </div>
                      )}
                      {fieldErrors.rooms && (
                        <p className="mt-1 text-[11px] text-rose-600">{fieldErrors.rooms}</p>
                      )}
                    </div>

                    {/* Two facts about the flat, answered the way every
                        other answer on this form is: a chip you tap
                        (owner, 2026-09-21: "I don't like the toggles on
                        mobile, they should be kind of different"). A
                        20 px switch is a poor target on a phone and the
                        odd one out next to the type row and the room
                        tiles; these are the same size as those, and the
                        rule above them gives the room row its space. */}
                    <div className="grid grid-cols-2 gap-1.5 border-t border-gray-100 pt-3">
                      {([
                        ["has_repair", form.has_repair, t("sell.renovated")],
                        ["has_bill_of_sale", form.has_bill_of_sale, t("sell.kupcha")],
                      ] as const).map(([key, on, label]) => (
                        <button
                          key={key}
                          type="button"
                          role="switch"
                          aria-checked={on}
                          onClick={() => update(key, !on)}
                          className={`flex h-9 items-center justify-center gap-1.5 rounded-lg border px-2 text-xs font-semibold transition-colors ${
                            on ? SELECTED : UNSELECTED
                          }`}
                        >
                          <Check className={`h-3.5 w-3.5 shrink-0 ${on ? "opacity-100" : "opacity-0"}`} />
                          <span className="truncate">{label}</span>
                          {/* Mirrors the tick so the label is centred in
                              both states and does not shift when tapped
                              (owner, 2026-09-21: "when the checkbox is not
                              there, it is not centered"). */}
                          <span className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* RIGHT - location: the field, then a preview box that
                    is on screen from the start (empty until something is
                    picked) so the map never shoulders the form aside. */}
                <div id="calc-f-location" className={`lg:border-l lg:border-gray-100 lg:pl-6 ${nudge > 0 && missing.location ? "rounded-xl ring-2 ring-rose-300 ring-offset-2" : ""}`}>
                  <LocationPicker value={location} onChange={setLocation} compact fillPreview valuation />
                </div>
              </div>

              {/* Desktop action row, inside the same card and sized to its
                  label rather than the full width (owner, 2026-09-21).
                  It sits directly under the location and stays disabled
                  until one is picked. No note explains that: the dashed
                  map placeholder invites the click and the dead button
                  says the rest (owner, 2026-09-21, twice). */}
              <div className="mx-auto mt-3 hidden w-full max-w-3xl border-t border-gray-100 pt-3 lg:flex lg:max-w-5xl lg:flex-col lg:items-center lg:justify-center lg:gap-1">
                <button
                  type="submit"
                  disabled={loading}
                  aria-disabled={!canSubmit}
                  className={`rounded-xl px-5 py-2 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 ${canSubmit ? "bg-indigo-600 hover:bg-indigo-700" : "bg-indigo-400 hover:bg-indigo-500"}`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      {t("sell.loading")}
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-1.5">
                      {t("sell.cta")}
                    </span>
                  )}
                </button>
                {missingNote}
              </div>
              </div>
            </form>

            <div className="max-w-3xl mx-auto">
              <RecentActivity kind="calculate" />
            </div>

            {/* Lift body bottom padding so the footer clears the sticky
                submit / rate-limit bar (sits 64-137 px above the screen
                bottom at < sm). Only mounted in the hero state because
                that's the only time the sticky bar is rendered; once a
                result shows, the bar is gone. */}
            <MobileBottomChromeOffset />

            {/* Mobile sticky submit - only on < lg. Mobile viewports
                aren't tall enough to fit a single-column form + CTA on
                one screen once LocationPicker expands (district →
                complex → map). `backdrop-blur` dropped (per-scroll
                paint cost - same cleanup as the nav bar); solid
                bg-white + border-t still visually anchors the bar.
                ``bottom`` is offset by tab-bar height + safe-area so
                the submit floats flush ABOVE the MobileTabBar
                (z-1100). Without this offset the tab bar covers the
                submit on every mobile viewport. 4rem matches the
                tab bar's actual rendered height (py-2.5 + w-6 icon +
                gap-1 + text-[11px]). */}
            <div className={`fixed bottom-0 left-0 right-0 z-40 border-t shadow-[0_-4px_12px_rgba(0,0,0,0.05)] lg:hidden sm:bottom-0 bottom-[calc(env(safe-area-inset-bottom)+4rem)] ${
              rateLimitCta ? "bg-amber-50 border-amber-200" : "bg-white border-gray-200"
            }`}>
              <div className="max-w-2xl mx-auto px-4 py-3">
                {rateLimitCta ? (
                  // Rate-limited path: replace the disabled-looking
                  // Evaluate button with the full mini-banner content -
                  // icon + headline + subtext + CTA - so mobile users
                  // who scroll past the top RateLimitBanner still see
                  // WHY the form can't be submitted, not just a Sign-up
                  // button with no context. Amber tint matches the
                  // banner card colour for visual continuity.
                  // Headline + button on one row, the explanation on
                  // its own full-width line under them, so the button
                  // never squeezes the text into a narrow column.
                  // amber-* text (not gray-*) stays legible in dark mode.
                  <div>
                    <div className="flex items-center gap-3">
                      <div className="shrink-0 w-9 h-9 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center">
                        <Lock className="w-4 h-4 text-amber-700" aria-hidden />
                      </div>
                      <p className="flex-1 min-w-0 text-sm font-bold text-amber-900 leading-snug">
                        {t("rate.freeused")}
                      </p>
                      <Link
                        href={rateLimitCta.href}
                        className="shrink-0 inline-flex items-center justify-center px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-semibold text-xs whitespace-nowrap hover:bg-indigo-700 transition-colors"
                      >
                        {t(rateLimitCta.labelKey)}
                      </Link>
                    </div>
                    <p className="mt-2 text-xs text-amber-800 leading-snug">
                      {t("rate.signinhint")}
                    </p>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit as unknown as React.MouseEventHandler<HTMLButtonElement>}
                    disabled={loading}
                    aria-disabled={!canSubmit}
                    className={`w-full py-3.5 text-white rounded-xl font-semibold text-sm disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors ${canSubmit ? "bg-indigo-600 hover:bg-indigo-700" : "bg-indigo-400 hover:bg-indigo-500"}`}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        {t("sell.loading")}
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-1.5">
                        {t("sell.cta")}
                      </span>
                    )}
                  </button>
                )}
                {!rateLimitCta && missingNote}
              </div>
            </div>
          </>
        ) : (
          <div>
            {/* The way BACK to the form is SellerResultCard's own action
                row (onReset), beside Share and Ask: without one, the 1h
                localStorage restore above locked this page into the old
                result on every visit (chat complaint, 2026-08-08). */}
            <SellerResultCard
              mode={result.mode || "sale"}
              predicted_price={result.predicted_price}
              clearing_price={result.clearing_price}
              tiers={result.tiers}
              ttsTiers={result.time_to_sell?.tiers ?? null}
              fair_value_low={result.fair_value_low}
              fair_value_high={result.fair_value_high}
              reasoning={result.reasoning}
              fact_checks={result.fact_checks}
              area_m2={result.area_m2}
              room_count={result.room_count}
              floor={result.floor}
              total_floors={result.total_floors}
              location_name={result.location_name}
              category_name={result.category_name}
              has_repair={result.has_repair}
              has_bill_of_sale={result.has_bill_of_sale}
              latitude={result.latitude}
              longitude={result.longitude}
              history_id={result.history_id}
              complex_name={result.complex_name}
              complex_cluster_id={result.complex_cluster_id}
              complex_median_m2={result.complex_median_m2}
              hex_median_m2={result.hex_median_m2}
              district_overall_ppm={result.district_overall_ppm}
              district_personalized_ppm={result.district_personalized_ppm}
              district_personalized_has_repair={result.district_personalized_has_repair}
              price_factors={result.price_factors}
              onReset={handleReset}
              rent={result.rent_result ? {
                predicted_price: result.rent_result.predicted_price,
                tiers: result.rent_result.tiers,
                fair_value_low: result.rent_result.fair_value_low,
                fair_value_high: result.rent_result.fair_value_high,
                reasoning: result.rent_result.reasoning,
                price_factors: result.rent_result.price_factors,
                house_rent: result.rent_result.house_rent,
              } : null}
              house_rent_refusal={result.house_rent_refusal}
              initialRentView={leadRent}
              notariat_estimate={result.notariat_estimate}
            />
          </div>
        )}
      </main>
  );
}
